// src/connection/state.ts

import type { ConnectionState } from '../types';

/**
 * Valid state transitions
 */
export const VALID_TRANSITIONS: Record<ConnectionState, ConnectionState[]> = {
  disconnected: ['connecting'],
  connecting: ['connected', 'error', 'reconnecting'],
  connected: ['connected', 'ready', 'error', 'reconnecting'],
  ready: ['ready', 'reconnecting', 'disconnected'],
  reconnecting: ['connecting', 'disconnected'],
  error: ['reconnecting', 'disconnected'],
};

/**
 * Check if state transition is valid
 */
export function isValidTransition(from: ConnectionState, to: ConnectionState): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * State machine for connection
 */
export class ConnectionStateMachine {
  private state: ConnectionState = 'disconnected';
  private listeners: Set<(state: ConnectionState) => void> = new Set();

  getState(): ConnectionState {
    return this.state;
  }

  transition(to: ConnectionState): boolean {
    if (this.state === to) {
      return true; // No-op
    }

    if (!isValidTransition(this.state, to)) {
      console.warn(`Invalid state transition: ${this.state} -> ${to}`);
      return false;
    }

    this.state = to;
    this.notifyListeners();
    return true;
  }

  onStateChange(listener: (state: ConnectionState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  reset(): void {
    this.state = 'disconnected';
    this.notifyListeners();
  }
}
